import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Hero from '../components/sections/Hero';
import FeaturedProducts from '../components/sections/FeaturedProducts';
import useSEO from '../hooks/useSEO';

const Home = () => {
  useSEO({
    title: 'Professional Water Treatment & Membrane Solutions',
    description: 'Shanghai Minipore Industrial Co.,Ltd. - ISO9001 certified manufacturer of RO membranes, UF membranes, filter housings, and complete water treatment systems. Serving 50+ countries.',
    keywords: 'Minipore, water treatment, RO membrane, UF membrane, filter housing, reverse osmosis, ultrafiltration, water purification, Shanghai',
    url: '/',
  });

  const stats = [
    { label: 'Years Experience', value: '30+' },
    { label: 'Countries Served', value: '50+' },
    { label: 'Product Lines', value: '10+' },
    { label: 'Happy Customers', value: '10000+' },
  ];

  return (
    <div className="pt-16">
      <Hero />
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <FeaturedProducts />
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">About Minipore</h2>
              <p className="text-lg text-gray-600 mb-6">
                Shanghai Minipore Industrial Co.,Ltd. is an export-oriented environmental protection enterprise specializing in raw water and sewage treatment consumables, accessories and equipment.
              </p>
              <p className="text-gray-600 mb-8">
                With an open mind and global vision, relying on a perfect procurement and sales network, we provide high-quality and affordable water treatment products and professional technical services, striving to build a one-stop procurement platform for the water treatment industry.
              </p>
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Located at:</h4>
                <p className="text-gray-600">
                  No 2755, Bao'an Highway, Malu Town, Jiading District,<br />
                  Shanghai, China. 201801
                </p>
              </div>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">华膜 Brand</div>
                <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">MINIPORE Brand</div>
                <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">MURFILTEC Brand</div>
              </div>
              <Link to="/about" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Learn More About Us
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <video
                src="https://cloud.sharemyimage.com/2026/07/06/normal_video-1.webm"
                className="rounded-2xl shadow-lg w-full h-auto"
                controls
                autoPlay
                muted
                loop
              />
            </motion.div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Start Your Water Treatment Project?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Contact our expert team today for professional consultation and customized solutions tailored to your specific needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors">
                Get Quote
              </Link>
              <Link to="/products" className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-colors">
                Browse Products
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;